import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Scale, AlertTriangle, XCircle } from 'lucide-react';
import { ProcurementItem } from '@/types/database/procurement-items';

interface ItemQualityInspectionSectionProps {
  items: ProcurementItem[];
  isPreliminary: boolean;
  isFinalComplete: boolean;
}

export const ItemQualityInspectionSection: React.FC<ItemQualityInspectionSectionProps> = ({
  items,
  isPreliminary,
  isFinalComplete
}) => {
  const [isOpen, setIsOpen] = React.useState(true);

  // Filter items that have quality data
  const itemsWithQuality = items.filter(item => item.item_quality);

  if (itemsWithQuality.length === 0) {
    return null;
  }

  // Calculate totals
  const totals = itemsWithQuality.reduce((acc, item) => {
    const quality = item.item_quality!;
    const usePreliminary = isPreliminary && !isFinalComplete;
    
    return {
      totalQuantity: acc.totalQuantity + (item.quantity || 0),
      totalDamage: acc.totalDamage + (usePreliminary ? quality.preliminary_damage_kg : quality.damage_kg),
      totalCompensation: acc.totalCompensation + (usePreliminary ? quality.preliminary_compensation_kg : quality.compensation_kg),
    };
  }, { totalQuantity: 0, totalDamage: 0, totalCompensation: 0 });

  const getGradeBadgeColor = (grade?: string) => {
    if (!grade) return 'bg-muted text-muted-foreground';
    const gradeUpper = grade.toUpperCase();
    if (gradeUpper === 'A' || gradeUpper === 'PREMIUM') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    if (gradeUpper === 'B' || gradeUpper === 'STANDARD') return 'bg-blue-100 text-blue-800 border-blue-200';
    if (gradeUpper === 'C' || gradeUpper === 'ECONOMY') return 'bg-amber-100 text-amber-800 border-amber-200';
    return 'bg-muted text-muted-foreground';
  };

  const getDamageSeverity = (damagePercent: number) => {
    if (damagePercent === 0) return 'none';
    if (damagePercent < 5) return 'low';
    if (damagePercent < 15) return 'moderate';
    return 'high';
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mt-4">
      <CollapsibleTrigger className="flex items-center gap-2 w-full text-left p-2 hover:bg-muted/50 rounded-lg transition-colors">
        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <Scale className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">Per-Item Quality Breakdown</span>
        <Badge variant="secondary" className="ml-auto">{itemsWithQuality.length} items</Badge>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="mt-3 space-y-3">
        {itemsWithQuality.map((item, index) => {
          const quality = item.item_quality!;
          const usePreliminary = isPreliminary && !isFinalComplete;
          const isRejected = quality.is_rejected === true;
          
          const damage = usePreliminary ? quality.preliminary_damage_kg : quality.damage_kg;
          const compensation = usePreliminary ? quality.preliminary_compensation_kg : quality.compensation_kg;
          const damagePercent = usePreliminary ? quality.preliminary_damage_percentage : quality.damage_percentage;
          const netPayable = isRejected ? 0 : Math.max(0, (item.quantity || 0) - compensation);
          const severity = getDamageSeverity(damagePercent);
          
          return (
            <Card 
              key={item.id} 
              className={`border-l-4 ${
                isRejected 
                  ? 'border-l-destructive bg-destructive/5' 
                  : 'border-l-primary/30'
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">#{index + 1}</span>
                    <span className={`font-medium ${isRejected ? 'line-through text-muted-foreground' : ''}`}>
                      {item.product_name}
                    </span>
                    {isRejected ? (
                      <Badge variant="destructive" className="flex items-center gap-1">
                        <XCircle className="h-3 w-3" />
                        Rejected
                      </Badge>
                    ) : quality.quality_grade ? (
                      <Badge variant="outline" className={getGradeBadgeColor(quality.quality_grade)}>
                        Grade {quality.quality_grade}
                      </Badge>
                    ) : null}
                  </div>
                  {!isRejected && severity === 'high' && (
                    <Badge variant="destructive" className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      High Damage
                    </Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="bg-muted/50 rounded p-2">
                    <p className="text-muted-foreground text-xs">Expected</p>
                    <p className={`font-medium ${isRejected ? 'line-through' : ''}`}>
                      {(item.quantity || 0).toFixed(2)} kg
                    </p>
                  </div>
                  <div className={`rounded p-2 ${
                    isRejected 
                      ? 'bg-destructive/10' 
                      : severity === 'high' 
                        ? 'bg-destructive/10' 
                        : severity === 'moderate' 
                          ? 'bg-amber-50' 
                          : 'bg-muted/50'
                  }`}>
                    <p className="text-muted-foreground text-xs">Damage</p>
                    <p className={`font-medium ${
                      isRejected || severity === 'high' 
                        ? 'text-destructive' 
                        : severity === 'moderate' 
                          ? 'text-amber-600' 
                          : ''
                    }`}>
                      {damage.toFixed(2)} kg ({damagePercent.toFixed(1)}%)
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded p-2">
                    <p className="text-muted-foreground text-xs">Compensation</p>
                    <p className="font-medium">{compensation.toFixed(2)} kg</p>
                  </div>
                </div>
                
                {quality.grade_notes && (
                  <p className="mt-2 text-sm text-muted-foreground italic">
                    Note: {quality.grade_notes}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
        
        {/* Totals Summary */}
        <Card className="bg-muted/30 border-2">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Total ({itemsWithQuality.length} items)</span>
              <div className="flex items-center gap-6 text-sm">
                <div className="text-center">
                  <p className="text-muted-foreground text-xs">Total Qty</p>
                  <p className="font-medium">{totals.totalQuantity.toFixed(2)} kg</p>
                </div>
                <div className="text-center">
                  <p className="text-muted-foreground text-xs">Total Damage</p>
                  <p className="font-medium">{totals.totalDamage.toFixed(2)} kg</p>
                </div>
                <div className="text-center">
                  <p className="text-muted-foreground text-xs">Total Compensation</p>
                  <p className="font-medium">{totals.totalCompensation.toFixed(2)} kg</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ItemQualityInspectionSection;
